import axiosInstance from '../../api/axios.js';

export type GeneratedControlSpec = {
  control_id?: string;
  control_title?: string;
  control_objective?: string;
  control_wording_text?: string;
  control_wording_raw?: string;
  frequency?: string;
  test_steps?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

export type GenerateTestStepsStreamHandlers = {
  onStatus?: (payload: { message?: string; detail?: string; icon?: string }) => void;
  onError?: (message: string) => void;
};

export async function generateTestStepsStream(
  requestBody: Record<string, unknown>,
  handlers: GenerateTestStepsStreamHandlers = {},
  signal?: AbortSignal
): Promise<GeneratedControlSpec> {
  const response = await axiosInstance.post('/api/controls/generate-test-steps-stream', requestBody, {
    adapter: 'fetch',
    responseType: 'stream',
    validateStatus: () => true,
    signal,
  });

  if (response.status === 403) {
    let message = 'An error occurred while generating test steps';
    try {
      const text = await new Response(response.data).text();
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || parsed.detail || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.data.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEventType = 'status';
  let result: GeneratedControlSpec | null = null;

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        throw new DOMException('Aborted', 'AbortError');
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEventType = line.slice(7).trim();
          continue;
        }
        if (!line.startsWith('data: ')) continue;

        try {
          const data = JSON.parse(line.slice(6));

          if (currentEventType === 'error') {
            const errorMsg = data.message || 'An error occurred while generating test steps';
            handlers.onError?.(errorMsg);
            throw new Error(errorMsg);
          }

          if (currentEventType === 'status') {
            handlers.onStatus?.(data);
          }

          if (currentEventType === 'result' && data.test_steps) {
            result = data as GeneratedControlSpec;
          }
        } catch (parseErr) {
          if (parseErr instanceof Error && !parseErr.message.includes('JSON')) {
            throw parseErr;
          }
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* reader may already be cancelled */
    }
  }

  if (!result?.test_steps?.length) {
    throw new Error('No test steps were generated. Please try again.');
  }

  return result;
}

export function buildGenerateTestStepsRequest(control: Record<string, unknown>): Record<string, unknown> {
  return {
    control_id: String(control.control_id ?? control.client_control_id ?? '').trim(),
    control_title: String(control.title ?? '').trim(),
    control_wording_raw: String(control.control_wording_text ?? control.control_wording ?? '').trim(),
    control_objective: String(control.objective ?? '').trim(),
    frequency: String(control.frequency ?? '').trim(),
  };
}
